import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { createDateTimeSecondFormatter } from '../utils/formatter';

function isPendingStatus(status) {
  const s = (status || '').toLowerCase();
  return ['pending', 'inprogress', 'in progress', 'running'].includes(s);
}

/** Parse output - may be JSON string from script summary */
function parseLoadSummary(output) {
  if (!output) return null;
  try {
    const obj = typeof output === 'string' ? JSON.parse(output) : output;
    if (obj && typeof obj === 'object' && ('total_files' in obj || 'folders_succeeded' in obj)) {
      return obj;
    }
  } catch (_) {}
  return null;
}

/** Format load summary for display */
function formatLoadSummary(summary) {
  const { total_files = 0, total_rows = 0, folders_succeeded = 0, folders_failed = 0, duration_seconds } = summary;
  const parts = [`${total_files} files`, `${total_rows} rows`];
  if (folders_succeeded !== undefined || folders_failed !== undefined) {
    parts.push(`${folders_succeeded} folders succeeded`);
    if (folders_failed > 0) parts.push(`${folders_failed} failed`);
  }
  if (duration_seconds != null) parts.push(`${duration_seconds}s`);
  return parts.join(' • ');
}

/** Parse folders array - handles various API formats */
function parseFolders(folders) {
  if (!folders) return [];
  if (Array.isArray(folders)) return folders.map((f) => (typeof f === 'string' ? f : String(f)).trim()).filter(Boolean);
  if (typeof folders === 'string') return folders.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

function FoldersCell({ folders, loadAll }) {
  const folderList = parseFolders(folders);
  const count = folderList.length;

  if (loadAll) return <span>All</span>;
  if (count === 0) return <span>—</span>;
  if (count === 1) return <span>{folderList[0]}</span>;

  return (
    <Tooltip
      title={
        <Typography
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            maxHeight: 320,
            overflowY: 'auto',
            display: 'block',
            py: 0.5,
          }}
        >
          {folderList.join('\n')}
        </Typography>
      }
      placement="left"
      PopperProps={{
        sx: {
          maxWidth: '50vw',
          '& .MuiTooltip-tooltip': {
            maxWidth: '30vw',
            backgroundColor: 'lightgray',
            outline: '1px darkgray solid',
            borderRadius: '10px',
          },
        },
        modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'help',
          color: 'primary.main',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        {count} folders
      </Box>
    </Tooltip>
  );
}

function OutputResultsCell({ output, status }) {
  if (isPendingStatus(status)) return '—';
  if (!output || (typeof output === 'string' && !output.trim())) return '—';
  const summary = parseLoadSummary(output);
  const content = summary
    ? formatLoadSummary(summary) + (summary.folder_results?.length ? '\n\n' + JSON.stringify(summary.folder_results, null, 2) : '')
    : (typeof output === 'object' ? JSON.stringify(output, null, 2) : String(output));
  return (
    <Tooltip
      title={
        <Typography
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            maxHeight: 320,
            overflowY: 'auto',
            display: 'block',
            py: 0.5,
          }}
        >
          {content}
        </Typography>
      }
      placement="left"
      PopperProps={{
        sx: {
          maxWidth: '50vw',
          '& .MuiTooltip-tooltip': {
            maxWidth: '30vw',
            backgroundColor: 'lightgray',
            outline: '1px darkgray solid',
            borderRadius: '10px',
          },
        },
        modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'help',
          color: 'primary.main',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        View results
      </Box>
    </Tooltip>
  );
}

function StatusChip({ status }) {
  const normalizedStatus = (status || '').toUpperCase();
  const isSuccess = normalizedStatus === 'SUCCESS';
  const isFailed = normalizedStatus === 'FAILED';
  const isPending = !isSuccess && !isFailed;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 1,
        fontSize: '0.8rem',
        fontWeight: 600,
        bgcolor: isSuccess ? 'success.light' : isFailed ? 'error.light' : 'warning.light',
        color: isSuccess ? 'success.contrastText' : isFailed ? 'error.contrastText' : 'warning.contrastText',
      }}
    >
      {isSuccess && <CheckCircleIcon sx={{ fontSize: 16 }} />}
      {isFailed && <ErrorIcon sx={{ fontSize: 16 }} />}
      {isPending && <ScheduleIcon sx={{ fontSize: 16 }} />}
      {status || 'pending'}
    </Box>
  );
}

const useOpenSearchLoadsTableCols = (timezone = 'America/New_York') => {
  const dateTimeSecondTzFormat = createDateTimeSecondFormatter(timezone);

  return [
    {
      accessorKey: 'executionId',
      header: 'Execution ID',
      size: 1,
      filterFn: 'contains',
      Cell: ({ cell }) => {
        const value = cell.getValue();
        const display = value ? String(value).slice(-9) : '';
        return (
          <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {display}
          </span>
        );
      },
    },
    {
      accessorKey: 'foldersDisplay',
      header: 'Folders',
      size: 1,
      filterFn: 'contains',
      Cell: ({ row }) => (
        <FoldersCell folders={row.original.folders} loadAll={row.original.loadAll} />
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 1,
      filterFn: 'equals',
      Cell: ({ cell }) => <StatusChip status={cell.getValue()} />,
    },
    {
      accessorKey: 'username',
      header: 'Started By',
      size: 1,
      filterFn: 'contains',
    },
    {
      accessorKey: 'createdAt',
      header: 'Started',
      size: 1,
      Cell: ({ cell }) => dateTimeSecondTzFormat(cell.getValue()),
    },
    {
      accessorKey: 'completedAt',
      header: 'Completed',
      size: 1,
      Cell: ({ cell }) => (cell.getValue() ? dateTimeSecondTzFormat(cell.getValue()) : '—'),
    },
    {
      accessorKey: 'results_cell',
      header: 'Results',
      size: 1,
      Cell: ({ row }) => (
        <OutputResultsCell output={row.original.output} status={row.original.status} />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      meta: { excludeFromExport: true },
    },
    {
      accessorKey: 'error_link',
      header: 'Error',
      size: 1,
      enableSorting: false,
      enableColumnFilter: false,
      meta: { excludeFromExport: true },
    },
  ];
};

export { useOpenSearchLoadsTableCols };
